import { audience as audienceContracts, canvas as canvasContracts } from '@bb/contracts';
import type { CanvasEntrySpec, CanvasSettings, CanvasValidationIssue } from '../../../api/client.ts';
import { deliveryControlsIssues } from '../../campaigns/schedule/deliveryControls.ts';
import { scheduleIssues } from '../../campaigns/schedule/scheduledDelivery.ts';
import { triggerTemplate, validateActionBased, type TriggerSpec } from '../../campaigns/trigger/actionBased.ts';

export type WizardStepId = 'basics' | 'entry' | 'target' | 'exit' | 'send' | 'build' | 'summary';

/** Steps of the Canvas wizard (BB-110) in Braze's order, with exit criteria after the target; conversions join with BB-103. */
export const WIZARD_STEPS: Array<{ id: WizardStepId; label: string }> = [
  { id: 'basics', label: 'Basics' },
  { id: 'entry', label: 'Entry' },
  { id: 'target', label: 'Target' },
  { id: 'exit', label: 'Exit' },
  { id: 'send', label: 'Send settings' },
  { id: 'build', label: 'Build' },
  { id: 'summary', label: 'Summary' },
];

export type Entry = NonNullable<CanvasEntrySpec>;
export type EntryType = Entry['type'];
export type ActionEntry = Extract<Entry, { type: 'action_based' }>;
export type LimitPeriod = (typeof canvasContracts.ENTRY_LIMIT_PERIODS)[number];
export type ExitEvent = CanvasSettings['exit']['events'][number];

export const MAX_ENTRY_TRIGGERS = 10;
export const MAX_EXIT_EVENTS = 10;
export const MAX_CORRELATIONS = 5;
export const MAX_REENTRY_SECONDS = 730 * 86_400;
export const MAX_ENTRY_LIMIT = 100_000_000;

/** Entry limit periods by entry type, as in Braze: a scheduled Canvas counts per run or ever; action and API entries per UTC hour, UTC day or ever. */
export const LIMIT_PERIODS: Record<EntryType, LimitPeriod[]> = {
  scheduled: ['run', 'lifetime'],
  action_based: ['hour', 'day', 'lifetime'],
  api_triggered: ['hour', 'day', 'lifetime'],
};

export const LIMIT_PERIOD_LABELS: Record<LimitPeriod, string> = { hour: 'per hour (UTC)', day: 'per day (UTC)', lifetime: 'in total, ever', run: 'per scheduled run' };

/** A new entry of a type: launch right away, one custom event to choose, or the API. */
export function newEntry(type: EntryType): Entry {
  switch (type) {
    case 'scheduled':
      return { type, schedule: { kind: 'immediate' } };
    case 'action_based':
      return { type, triggers: [triggerTemplate('custom_event')], window: {} };
    case 'api_triggered':
      return { type };
  }
}

/** Another entry type (or the one used before, `remembered`); an entry limit period the new type does not have becomes «ever». */
export function withEntryType(settings: CanvasSettings, type: EntryType, remembered?: Entry): CanvasSettings {
  const entry = remembered?.type === type ? remembered : newEntry(type);
  const { limit } = settings.controls;
  const controls = limit && !LIMIT_PERIODS[type].includes(limit.per) ? { ...settings.controls, limit: { ...limit, per: 'lifetime' as const } } : settings.controls;
  return { ...settings, entry, controls };
}

/** One problem of the settings form: its path in `CanvasSettings`, the wizard step it is on and what to do. */
export interface SettingsIssue {
  step: 'entry' | 'target' | 'exit' | 'send';
  path: Array<string | number>;
  message: string;
}

const stepOfPath = (path: ReadonlyArray<PropertyKey>): SettingsIssue['step'] => (path[0] === 'exit' ? 'exit' : path[0] === 'send' ? 'send' : path[0] === 'audience' || path[0] === 'controls' ? 'target' : 'entry');

/** Quiet hours and rate of the Canvas checked as the campaign delivery controls they share (BB-102), keyed `quietHours.to`, `ratePerMinute`… */
export function sendIssues(send: CanvasSettings['send']): Record<string, string> {
  const { quietHours, ratePerMinute, rateMaxDelayHours } = send;
  return deliveryControlsIssues({ ...(quietHours ? { quietHours } : {}), ...(ratePerMinute !== undefined ? { ratePerMinute } : {}), ...(rateMaxDelayHours !== undefined ? { rateMaxDelayHours } : {}) });
}

/** Trigger issues of an action-based check, as if `list` were the triggers of a campaign. */
const triggerIssues = (list: TriggerSpec[]) => (list.length ? validateActionBased({ triggers: list, delay: { kind: 'none' }, cancelOn: [], reevaluateAudienceAtSend: false }).issues : []);

const audienceComplete = (value: audienceContracts.AudienceDefinition | null) => value === null || audienceContracts.AudienceDefinition.safeParse(value).success;

const CONTROL_MESSAGES: Record<string, string> = {
  windowSeconds: 'Enter a re-entry window from 0 to 730 days.',
  count: `Enter a limit from 1 to ${MAX_ENTRY_LIMIT.toLocaleString('en-US')}.`,
  per: 'Choose a period.',
};

/**
 * What would stop the settings from saving, or the Canvas from launching, with a message for the form: the entry type,
 * the triggers and their filters (the campaign checks of BB-100), the schedule (BB-99), the window, the controls,
 * the audiences and the exit events with their correlations. The contract (`canvas.CanvasSettings`) is checked last, so
 * nothing the server would refuse slips through.
 */
export function settingsIssues(settings: CanvasSettings): SettingsIssue[] {
  const issues: SettingsIssue[] = [];
  const { entry } = settings;
  if (!entry) issues.push({ step: 'entry', path: ['entry'], message: 'Choose how users enter the Canvas.' });
  else if (entry.type === 'scheduled') {
    for (const [field, message] of Object.entries(scheduleIssues(entry.schedule))) issues.push({ step: 'entry', path: ['entry', 'schedule', field], message });
  } else if (entry.type === 'action_based') {
    for (const issue of triggerIssues(entry.triggers)) issues.push({ step: 'entry', path: ['entry', ...issue.path], message: issue.message });
    const { startAt, endAt } = entry.window;
    if (startAt && endAt && Date.parse(endAt) <= Date.parse(startAt)) issues.push({ step: 'entry', path: ['entry', 'window', 'endAt'], message: 'The end must be after the start.' });
  }
  if (entry && settings.controls.limit && !LIMIT_PERIODS[entry.type].includes(settings.controls.limit.per)) issues.push({ step: 'target', path: ['controls', 'limit', 'per'], message: 'Choose a period this entry type counts.' });
  const controls = canvasContracts.EntryControls.safeParse(settings.controls);
  if (!controls.success) {
    for (const issue of controls.error.issues) {
      const field = String(issue.path[issue.path.length - 1] ?? '');
      issues.push({ step: 'target', path: ['controls', ...issue.path.filter((p): p is string | number => typeof p !== 'symbol')], message: CONTROL_MESSAGES[field] ?? issue.message });
    }
  }
  if (!audienceComplete(settings.audience)) issues.push({ step: 'target', path: ['audience'], message: 'The entry audience is not complete yet: see its summary.' });
  issues.push(
    ...triggerIssues(settings.exit.events.map((e) => e.trigger)).map((issue): SettingsIssue => {
      const [, index, ...rest] = issue.path;
      return { step: 'exit', path: ['exit', 'events', index!, 'trigger', ...rest], message: issue.message };
    }),
  );
  settings.exit.events.forEach((event, i) =>
    event.correlate.forEach((row, j) => {
      if (!row.property.trim()) issues.push({ step: 'exit', path: ['exit', 'events', i, 'correlate', j, 'property'], message: 'Enter an event property.' });
      if (!row.contextKey.trim()) issues.push({ step: 'exit', path: ['exit', 'events', i, 'correlate', j, 'contextKey'], message: 'Enter a context key.' });
    }),
  );
  if (!audienceComplete(settings.exit.audience)) issues.push({ step: 'exit', path: ['exit', 'audience'], message: 'The exit audience is not complete yet: see its summary.' });
  for (const [field, message] of Object.entries(sendIssues(settings.send))) issues.push({ step: 'send', path: ['send', ...field.split('.')], message });
  const parsed = canvasContracts.CanvasSettings.safeParse(settings);
  if (!parsed.success && issues.length === 0) {
    for (const issue of parsed.error.issues) issues.push({ step: stepOfPath(issue.path), path: issue.path.filter((p): p is string | number => typeof p !== 'symbol'), message: `${issue.path.join('.')}: ${issue.message}` });
  }
  return issues;
}

/** Issues at this path or below it, with the prefix dropped (the forms of one step know their own paths). */
export function issuesBelow(issues: readonly SettingsIssue[], ...prefix: Array<string | number>): Array<{ path: Array<string | number>; message: string }> {
  return issues.filter((i) => prefix.every((p, k) => i.path[k] === p)).map((i) => ({ path: i.path.slice(prefix.length), message: i.message }));
}

/** Messages of the issues at exactly this path. */
export const messagesAt = (issues: readonly SettingsIssue[], ...path: Array<string | number>): string[] =>
  issues.filter((i) => i.path.length === path.length && path.every((p, k) => i.path[k] === p)).map((i) => i.message);

function collectFilters(node: audienceContracts.FilterNode, into: audienceContracts.FilterSpec[]) {
  if (node.kind === 'filter') into.push(node.filter);
  else if (node.kind === 'not') collectFilters(node.child, into);
  else node.children.forEach((child) => collectFilters(child, into));
}

/**
 * Events and attributes that both enter users (action triggers) and filter the entry audience. The audience is checked
 * with the data of the batch that brought the trigger, so the triggering event already counts there.
 */
export function triggerFilterOverlap(settings: CanvasSettings): string[] {
  if (settings.entry?.type !== 'action_based' || !settings.audience) return [];
  const filters: audienceContracts.FilterSpec[] = [];
  collectFilters(settings.audience.include, filters);
  settings.audience.exclude.forEach((node) => collectFilters(node, filters));
  const filtered = new Set(filters.flatMap((f) => ('event' in f ? [`event:${f.event}`] : f.type === 'custom_attribute' ? [`attribute:${f.key}`] : [])));
  const overlap = settings.entry.triggers.flatMap((t) => (t.kind === 'custom_event' && t.event && filtered.has(`event:${t.event}`) ? [t.event] : t.kind === 'attribute_changed' && t.attribute && filtered.has(`attribute:${t.attribute}`) ? [t.attribute] : []));
  return [...new Set(overlap)];
}

/**
 * The wizard step a server issue belongs to: `section` of the aggregated validation; exit settings come as `entry`
 * settings (the contract has no `exit` section yet), graph issues have none.
 */
export function stepOfIssue(issue: CanvasValidationIssue): WizardStepId {
  const section: string | undefined = issue.section;
  if (section === 'exit' || section === 'send') return section;
  if (issue.section === 'audience' || issue.section === 'controls') return 'target';
  if (issue.section === 'entry') return issue.code === 'invalid_settings' && issue.message.startsWith('exit') ? 'exit' : issue.code === 'invalid_settings' && issue.message.startsWith('send') ? 'send' : 'entry';
  return 'build';
}

/** Codes of the server issues that come from the stored settings, replaced by the form's own checks while the settings are unsaved. */
export const SETTINGS_ISSUE_CODES = new Set(['entry_missing', 'invalid_settings']);

/** «choose how users enter» → «Choose how users enter.» */
export const sentence = (message: string) => {
  const text = message.charAt(0).toUpperCase() + message.slice(1);
  return /[.!?]$/.test(text) ? text : `${text}.`;
};

const pad = (n: number) => String(n).padStart(2, '0');

/** An instant as the value of a `datetime-local` input, in the browser's time zone. */
export function toLocalInput(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** A `datetime-local` value (browser time zone) as an ISO instant; empty or invalid is no value. */
export function fromLocalInput(value: string): string | undefined {
  if (!value) return undefined;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

const KIND_TEXT: Record<TriggerSpec['kind'], (t: TriggerSpec) => string> = {
  custom_event: (t) => `performs ${(t as Extract<TriggerSpec, { kind: 'custom_event' }>).event || 'a custom event'}`,
  session_start: () => 'starts a session',
  attribute_changed: (t) => `changes ${(t as Extract<TriggerSpec, { kind: 'attribute_changed' }>).attribute || 'an attribute'}`,
  subscription_changed: () => 'changes a subscription group state',
  message_interaction: () => 'interacts with another campaign',
};

/** One line about how users enter, for the Summary. */
export function describeEntry(entry: Entry | null): string {
  if (!entry) return 'Not chosen yet.';
  if (entry.type === 'api_triggered') return 'API-triggered: your backend enters users through the Canvas Trigger API.';
  if (entry.type === 'action_based') {
    const window = [entry.window.startAt && `from ${new Date(entry.window.startAt).toLocaleString('en-GB')}`, entry.window.endAt && `until ${new Date(entry.window.endAt).toLocaleString('en-GB')}`].filter(Boolean).join(' ');
    return `Action-based: when the user ${entry.triggers.map((t) => KIND_TEXT[t.kind](t)).join(' or ')}${window ? `, ${window}` : ''}.`;
  }
  const s = entry.schedule;
  const local = 'localTime' in s && s.localTime ? " in users' local time" : '';
  if (s.kind === 'immediate') return 'Scheduled: once, when the Canvas launches.';
  if (s.kind === 'at') return `Scheduled: once on ${s.date} at ${s.time}${local}.`;
  const every = s.interval > 1 ? `every ${s.interval} ${s.every}s` : `every ${s.every}`;
  return `Scheduled: ${every} at ${s.time}${local}, from ${s.startDate}${s.endDate ? ` to ${s.endDate}` : ''}.`;
}

/** One line about the send settings, for the Summary. */
export function describeSend(send: CanvasSettings['send']): string {
  const parts = [
    send.quietHours?.enabled ? `quiet hours ${send.quietHours.from}–${send.quietHours.to}` : 'no quiet hours',
    send.ratePerMinute ? `at most ${send.ratePerMinute.toLocaleString('en-US')} sends per minute` : 'no Canvas rate limit',
  ];
  const skipped = Object.entries(send.exemptions).filter(([flag, on]) => flag !== 'countTowardsCaps' && on === true).length;
  if (skipped) parts.push(`${skipped} ${skipped === 1 ? 'exemption' : 'exemptions'}`);
  const text = parts.join(', ');
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}.`;
}
